import CIcon from "@coreui/icons-react";
import { cilMenu } from "@coreui/icons";
import { KeyboardArrowDown } from "@mui/icons-material";
import { Button } from "antd";
import { MouseEvent } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { LogoHorizontalSVG } from "../../../../assets/svg/SvgIcons";
import { URL_BOOKINGS, URL_LOGIN } from "../../../../utils/urls";
import { GenericDrawer } from "../../../common/GenericDrawer";
import { primaryColor } from "../../../styled/Theme";

const smoothScrollTo = (elementId: string) => {
  const element = document.getElementById(elementId);

  if (!element) return;

  const headerOffset = 94;
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

  window.scrollTo({
    top: offsetPosition,
    behavior: "smooth",
  });
};

type NavItem = {
  label: string;
  path: string;
  action?: () => void;
  children?: Array<{ label: string; path: string }>;
};

const navLinks: NavItem[] = [
  { label: "Home", path: "#home", action: () => smoothScrollTo("home") },
  {
    label: "Services",
    path: "#services",
    action: () => smoothScrollTo("services"),
  },
  {
    label: "Payments",
    path: "#payments",
    action: () => smoothScrollTo("payments"),
  },
  { label: "About", path: "#about", action: () => smoothScrollTo("about") },
  {
    label: "Contact",
    path: "#contact-us",
    action: () => smoothScrollTo("contact-us"),
  },
];

const handleNavClick = (
  event: MouseEvent<HTMLAnchorElement>,
  item: NavItem,
) => {
  if (item.action) {
    event.preventDefault();
    item.action();
    return;
  }

  if (item.path.startsWith("#")) {
    event.preventDefault();
    smoothScrollTo(item.path.replace("#", ""));
  }
};

const NavLink = ({
  item,
  className,
}: {
  item: NavItem;
  className?: string;
}) => {
  const { label, path, children } = item;

  return (
    <NavLinkWrapper
      className={className}
      href={path}
      onClick={(event) => handleNavClick(event, item)}
      target={path.includes("https") ? "_blank" : "_self"}
      rel={path.includes("https") ? "noopener noreferrer" : undefined}
    >
      <span>
        {label} {children ? <KeyboardArrowDown fontSize="small" /> : null}
      </span>
      {children ? (
        <div className="dropdown-content animate">
          {children.map((child) => (
            <a key={child.path} className="animate" href={child.path}>
              {child.label}
            </a>
          ))}
        </div>
      ) : null}
    </NavLinkWrapper>
  );
};

const TopBar = () => {
  const ctaButtons = (
    <TopBarActions className="flexCenter gap10">
      <Button className="topbar-btn topbar-btn-secondary" type="default">
        <Link to={`${URL_BOOKINGS}/create`}>Reserve Spot</Link>
      </Button>
      <Button className="topbar-btn topbar-btn-primary" type="primary">
        <Link to={URL_LOGIN}>Sign In</Link>
      </Button>
    </TopBarActions>
  );

  return (
    <Wrapper>
      <div className="container">
        <div className="nav-shell" id="nav">
          <BrandLink to="/" aria-label="Go to Smart Parking home">
            <img
              src={LogoHorizontalSVG}
              width={122}
              height={34}
              alt="Smart Parking"
            />
          </BrandLink>

          <DesktopNav id="desktop-links">
            {navLinks.map((link) => (
              <NavLink key={link.label} item={link} className="black" />
            ))}
          </DesktopNav>

          <DesktopButtons id="desktop-actions">{ctaButtons}</DesktopButtons>

          <MobileNav>
            <GenericDrawer
              title="Menu"
              placement="right"
              button={
                <MobileNavTrigger
                  id="mobile-nav-trigger"
                  className="flexCenter"
                  role="button"
                >
                  <CIcon
                    icon={cilMenu}
                    width={18}
                    style={{ color: primaryColor }}
                  />
                </MobileNavTrigger>
              }
            >
              <MobileDrawer>
                <div className="drawer-actions">{ctaButtons}</div>
                <div className="drawer-links">
                  {navLinks.map((link) => (
                    <a key={`mobile-${link.label}`} href={link.path}>
                      {link.label}
                    </a>
                  ))}
                </div>
              </MobileDrawer>
            </GenericDrawer>
          </MobileNav>
        </div>
      </div>
    </Wrapper>
  );
};

export default TopBar;

/**
 * The bar floats over the hero but stays fully opaque — no blur or
 * translucency — so the hero artwork never bleeds through the navigation.
 *
 * The pill sits inside .container rather than being the container, so its
 * outer edges line up exactly with the section content below it.
 */
const Wrapper = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 16px 0;
  background: transparent;
  pointer-events: none;

  .nav-shell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 68px;
    gap: 24px;
    padding: 0 12px 0 20px;
    border-radius: var(--lp-r-pill);
    border: 1px solid var(--lp-border);
    background: var(--lp-raised);
    box-shadow: var(--lp-shadow-md);
    pointer-events: auto;
  }

  @media (max-width: 1024px) {
    #desktop-links,
    #desktop-actions {
      display: none;
    }

    .nav-shell {
      padding: 0 12px 0 16px;
    }
  }
`;

const BrandLink = styled(Link)`
  display: inline-flex;
  align-items: center;
`;

const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  margin-left: auto;
`;

const DesktopButtons = styled.div`
  .topbar-btn {
    height: 40px;
    border-radius: 999px;
    padding-inline: 16px;
    font-size: 13px;
    font-weight: 600;
    border: 1px solid transparent;

    a {
      color: inherit;
      font-weight: 600;
    }
  }

  .topbar-btn-secondary {
    border-color: var(--lp-border);
    color: var(--lp-text);
    background: #fff;

    &:hover {
      border-color: ${primaryColor} !important;
      color: ${primaryColor} !important;
      background: #fff !important;
    }
  }

  .topbar-btn-primary {
    background: ${primaryColor};

    &:hover {
      background: var(--lp-primary-hover) !important;
    }
  }
`;

const TopBarActions = styled.div`
  flex-wrap: wrap;
`;

const NavLinkWrapper = styled.a`
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  color: var(--lp-muted);
  padding: 10px 0;
  position: relative;
  display: inline-flex;
  align-items: center;

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -11px;
    height: 2px;
    transform: scaleX(0);
    transform-origin: center;
    background: ${primaryColor};
    transition: transform 0.2s ease;
  }

  &:hover {
    color: ${primaryColor};
  }

  &:hover::after {
    transform: scaleX(1);
  }

  .dropdown-content {
    display: none;
    position: absolute;
    top: calc(100% + 10px);
    left: 0;
    min-width: 210px;
    border-radius: 12px;
    border: 1px solid rgba(33, 90, 169, 0.14);
    background: #fff;
    // box-shadow: 0 20px 45px rgba(13, 33, 64, 0.12);
    overflow: hidden;
    z-index: 4;

    a {
      color: #294062;
      padding: 12px 14px;
      text-decoration: none;
      display: block;
      font-size: 13px;

      &:hover {
        color: ${primaryColor};
        background: #f2f7ff;
      }
    }
  }

  &:hover .dropdown-content {
    display: block;
  }
`;

/**
 * GenericDrawer wraps its trigger in an unclassed div. Hiding only the
 * trigger would leave that wrapper as a zero-width flex item, still claiming
 * a gap and pushing the desktop buttons off the pill's right padding — so the
 * whole thing is taken out of the layout above the mobile breakpoint.
 */
const MobileNav = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const MobileNavTrigger = styled.div`
  width: 40px;
  height: 40px;
  border: 1px solid var(--lp-border);
  border-radius: 12px;
  background: #fff;
  display: flex;
`;

const MobileDrawer = styled.div`
  .drawer-actions {
    padding: 20px;
    border-bottom: 1px solid #edf2fb;

    .topbar-btn {
      width: 100%;
    }
  }

  .drawer-links {
    padding: 8px 20px 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    a {
      padding: 12px 2px;
      border-bottom: 1px solid #f0f3f8;
    }

    a::after {
      bottom: 0;
    }
  }
`;
